import type { ComplianceItem } from '../../types/dataops';

const now = '2026-02-11';

export const SEED_COMPLIANCE_ITEMS: ComplianceItem[] = [
  // ── USER DATA RIGHTS (System 1) ──
  {
    id: 'comp-del',
    category: 'user_data_rights',
    label: 'Data Deletion Pipeline',
    description: 'Full cascade deletion: Firestore docs, Storage uploads, Auth account, third-party notifications, anonymized purchase retention, completion proof generation.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Right to Deletion',
    regulatoryBasis: ['MHMDA', 'CCPA', 'CTDPA'],
    sla: '30 days',
    implementationPhase: 2,
    firestoreCollection: 'dataops_requests',
    cloudFunction: 'process-deletion',
    lastVerified: null,
    notes: '12-step cascade with dependency ordering. Must handle partial failures gracefully.',
    sortOrder: 1,
    updatedAt: now,
  },
  {
    id: 'comp-acc',
    category: 'user_data_rights',
    label: 'Data Access Requests',
    description: 'Generate downloadable package of all user data: profile, lab results, AI analyses, chat transcripts, consent history, sharing log.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Right to Access',
    regulatoryBasis: ['CCPA', 'CTDPA'],
    sla: '30 days',
    implementationPhase: 2,
    firestoreCollection: 'dataops_requests',
    cloudFunction: 'generate-export',
    lastVerified: null,
    notes: 'Export as JSON + CSV. Signed URL valid 7 days. Includes README.txt.',
    sortOrder: 2,
    updatedAt: now,
  },
  {
    id: 'comp-port',
    category: 'user_data_rights',
    label: 'Data Portability Export',
    description: 'Machine-readable export in JSON/CSV format for data portability rights.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Right to Portability',
    regulatoryBasis: ['CCPA'],
    sla: '30 days',
    implementationPhase: 2,
    firestoreCollection: 'dataops_requests',
    cloudFunction: 'generate-export',
    lastVerified: null,
    notes: 'Shares pipeline with access requests. Different format options.',
    sortOrder: 3,
    updatedAt: now,
  },
  {
    id: 'comp-corr',
    category: 'user_data_rights',
    label: 'Data Correction Requests',
    description: 'Allow users to request corrections to their personal information with change tracking.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Right to Correction',
    regulatoryBasis: ['CCPA', 'CTDPA'],
    sla: '30 days',
    implementationPhase: 2,
    firestoreCollection: 'dataops_requests',
    cloudFunction: null,
    lastVerified: null,
    notes: 'Manual review by admin. Track old/new values for audit trail.',
    sortOrder: 4,
    updatedAt: now,
  },
  {
    id: 'comp-opt',
    category: 'user_data_rights',
    label: 'Marketing Opt-Out',
    description: 'Immediate toggle to opt out of marketing communications. 10 business day SLA for CAN-SPAM.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Right to Opt-Out',
    regulatoryBasis: ['CAN-SPAM'],
    sla: '10 business days',
    implementationPhase: 2,
    firestoreCollection: 'dataops_consent',
    cloudFunction: null,
    lastVerified: null,
    notes: 'In-app toggle + email unsubscribe link. Immediate processing preferred.',
    sortOrder: 5,
    updatedAt: now,
  },
  {
    id: 'comp-consent-withdraw',
    category: 'user_data_rights',
    label: 'Consent Withdrawal Pipeline',
    description: 'Immediate processing when user withdraws consent. Stops all processing for that purpose, notifies third parties.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Consent Withdrawal',
    regulatoryBasis: ['MHMDA'],
    sla: 'Immediate',
    implementationPhase: 2,
    firestoreCollection: 'dataops_consent',
    cloudFunction: null,
    lastVerified: null,
    notes: 'Required consent withdrawal triggers account deactivation flow.',
    sortOrder: 6,
    updatedAt: now,
  },

  // ── CHILD SAFETY (System 2) ──
  {
    id: 'comp-age-gate',
    category: 'child_safety',
    label: 'Age Gate at Signup',
    description: 'Required DOB field during account creation. Hard block if age < 18. Device/email flagged for 24h to prevent retry.',
    status: 'operational',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §9 — Children\'s Privacy',
    regulatoryBasis: ['COPPA', 'TOS §3'],
    sla: null,
    implementationPhase: 1,
    firestoreCollection: null,
    cloudFunction: null,
    lastVerified: now,
    notes: 'TriageScreen validates 18+, 24h localStorage block on under-18, no retry. Audit logged.',
    sortOrder: 10,
    updatedAt: now,
  },
  {
    id: 'comp-minor-scan',
    category: 'child_safety',
    label: 'Minor Detection Scanner',
    description: 'Daily scan of chat transcripts and profile data for signals indicating user is under 18.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §9 — Children\'s Privacy',
    regulatoryBasis: ['COPPA'],
    sla: null,
    implementationPhase: 2,
    firestoreCollection: 'dataops_minor_detections',
    cloudFunction: 'detect-minors',
    lastVerified: null,
    notes: 'Pattern matching on chat transcripts. Three detection layers: age gate, ongoing scan, manual report.',
    sortOrder: 11,
    updatedAt: now,
  },
  {
    id: 'comp-minor-response',
    category: 'child_safety',
    label: 'Minor Detection Response',
    description: 'Immediate account suspension on detection. Full data deletion within 24h if confirmed. Email blocklist for 365 days.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §9 — Children\'s Privacy',
    regulatoryBasis: ['COPPA'],
    sla: '1 hour suspension, 24h deletion',
    implementationPhase: 2,
    firestoreCollection: 'dataops_minor_detections',
    cloudFunction: 'handle-minor-detection',
    lastVerified: null,
    notes: 'Skip cooling-off period. Immediate full cascade deletion if confirmed.',
    sortOrder: 12,
    updatedAt: now,
  },

  // ── CONSENT MANAGEMENT (System 3) ──
  {
    id: 'comp-optin',
    category: 'consent_management',
    label: 'Opt-In Consent Collection',
    description: 'Affirmative opt-in consent before lab upload. Per-purpose consent with exact text recorded.',
    status: 'operational',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §2 — Consent',
    regulatoryBasis: ['MHMDA', 'CTDPA'],
    sla: null,
    implementationPhase: 1,
    firestoreCollection: 'dataops_consent',
    cloudFunction: null,
    lastVerified: now,
    notes: 'TriageScreen consent step: 2 required + 3 optional. Records consent text, timestamp, method, user agent, policy version to Firestore.',
    sortOrder: 20,
    updatedAt: now,
  },
  {
    id: 'comp-granular',
    category: 'consent_management',
    label: 'Granular Consent Purposes',
    description: 'Separate consent toggles for 7 purposes: lab data, health profile, supplement recs, provider sharing, pharmacy sharing, marketing, analytics.',
    status: 'operational',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §2 — Consent',
    regulatoryBasis: ['MHMDA'],
    sla: null,
    implementationPhase: 2,
    firestoreCollection: 'dataops_consent',
    cloudFunction: null,
    lastVerified: now,
    notes: 'All 7 purposes in PrivacySettings with toggles. Required purposes show "Always on".',
    sortOrder: 21,
    updatedAt: now,
  },
  {
    id: 'comp-consent-ui',
    category: 'consent_management',
    label: 'Consent Management UI',
    description: 'User-facing /settings/privacy page with consent toggles, deletion request, data export, and correction form.',
    status: 'operational',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §6 — Your Rights',
    regulatoryBasis: ['MHMDA', 'CCPA', 'CTDPA'],
    sla: null,
    implementationPhase: 1,
    firestoreCollection: null,
    cloudFunction: null,
    lastVerified: now,
    notes: 'PrivacySettings page at /settings/privacy. Consent toggles, Download My Data, Delete My Account with DELETE confirmation.',
    sortOrder: 22,
    updatedAt: now,
  },

  // ── DATA RETENTION (System 4) ──
  {
    id: 'comp-chat-cleanup',
    category: 'data_retention',
    label: 'Chat Transcript Cleanup',
    description: 'Auto-delete chat transcripts older than 365 days. Generate anonymized summary before deletion.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §5 — Data Retention',
    regulatoryBasis: ['MHMDA'],
    sla: null,
    implementationPhase: 3,
    firestoreCollection: 'dataops_retention_jobs',
    cloudFunction: 'retention-cleanup',
    lastVerified: null,
    notes: 'Daily cron at 4 AM UTC. Strip all PII, store anonymized summary.',
    sortOrder: 30,
    updatedAt: now,
  },
  {
    id: 'comp-inactive',
    category: 'data_retention',
    label: 'Inactive User Data Purge',
    description: 'Detect users with no login for 730 days. Send 30-day warning email, then auto-delete if no login.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §5 — Data Retention',
    regulatoryBasis: ['MHMDA'],
    sla: null,
    implementationPhase: 3,
    firestoreCollection: 'dataops_retention_jobs',
    cloudFunction: 'retention-cleanup',
    lastVerified: null,
    notes: '2-year inactivity threshold. Warning email at day 0, deletion at day 30.',
    sortOrder: 31,
    updatedAt: now,
  },
  {
    id: 'comp-session-purge',
    category: 'data_retention',
    label: 'Session Data Purge',
    description: 'Auto-purge expired sessions older than 30 days.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §5 — Data Retention',
    regulatoryBasis: [],
    sla: null,
    implementationPhase: 3,
    firestoreCollection: 'dataops_retention_jobs',
    cloudFunction: 'retention-cleanup',
    lastVerified: null,
    notes: 'Nightly cleanup. Low priority but good hygiene.',
    sortOrder: 32,
    updatedAt: now,
  },
  {
    id: 'comp-backup-rotation',
    category: 'data_retention',
    label: 'Backup Rotation',
    description: '90-day rolling backup window. Auto-delete oldest backups. Verify backup chain integrity.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §5 — Data Retention',
    regulatoryBasis: [],
    sla: null,
    implementationPhase: 3,
    firestoreCollection: 'dataops_retention_jobs',
    cloudFunction: 'retention-cleanup',
    lastVerified: null,
    notes: 'Alert admin if backup gap detected. Deleted users excluded from next cycle.',
    sortOrder: 33,
    updatedAt: now,
  },

  // ── SECURITY & AUDIT (System 5) ──
  {
    id: 'comp-audit-log',
    category: 'security_audit',
    label: 'Audit Logging Infrastructure',
    description: 'Log every action touching user data: views, exports, modifications, deletions, sharing events. HIPAA-grade.',
    status: 'operational',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §4 — Security',
    regulatoryBasis: ['HIPAA', 'MHMDA'],
    sla: null,
    implementationPhase: 1,
    firestoreCollection: 'dataops_audit_log',
    cloudFunction: null,
    lastVerified: now,
    notes: 'logAudit() fire-and-forget utility. 18 action types. Wired into triage, consent, lab upload. Writes to dataops_audit_log.',
    sortOrder: 40,
    updatedAt: now,
  },
  {
    id: 'comp-breach-detect',
    category: 'security_audit',
    label: 'Breach Detection System',
    description: 'Automated scanning every 6 hours for: mass data access, unauthorized PHI access, unusual login patterns, data exfiltration.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §4 — Security',
    regulatoryBasis: ['HIPAA', 'FTC HBNR'],
    sla: null,
    implementationPhase: 3,
    firestoreCollection: 'dataops_breach_events',
    cloudFunction: 'breach-scan',
    lastVerified: null,
    notes: '6 breach signals: mass access (>50 records/hr), mass export, unauthorized PHI, unusual login, API abuse, exfiltration.',
    sortOrder: 41,
    updatedAt: now,
  },
  {
    id: 'comp-breach-response',
    category: 'security_audit',
    label: 'Breach Response Pipeline',
    description: 'Automated lockdown, assessment, containment, and notification pipeline. FTC 60-day notification. HIPAA HHS notification.',
    status: 'not_started',
    owner: 'Legal',
    policyRef: 'Privacy Policy §4 — Security',
    regulatoryBasis: ['HIPAA', 'FTC HBNR'],
    sla: '60 days notification',
    implementationPhase: 3,
    firestoreCollection: 'dataops_breach_events',
    cloudFunction: 'breach-alert',
    lastVerified: null,
    notes: 'If 500+ users: notify FTC in 10 biz days, media, website notice 60 days. If PHI: notify HHS + CC Medical.',
    sortOrder: 42,
    updatedAt: now,
  },

  // ── THIRD-PARTY SHARING (System 6) ──
  {
    id: 'comp-share-log',
    category: 'third_party_sharing',
    label: 'Data Sharing Audit Log',
    description: 'Log every instance where user data leaves Hemologix to a third party. Included in user data exports.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §3 — Data Sharing',
    regulatoryBasis: ['MHMDA'],
    sla: null,
    implementationPhase: 2,
    firestoreCollection: 'dataops_third_party_shares',
    cloudFunction: null,
    lastVerified: null,
    notes: 'Log: recipient, data categories, purpose, legal basis, consent record link, method, confirmation.',
    sortOrder: 50,
    updatedAt: now,
  },
  {
    id: 'comp-vendor-track',
    category: 'third_party_sharing',
    label: 'Vendor & BAA Tracking',
    description: 'Track BAA/DPA status for each third-party processor. Alert when agreements expire or are missing.',
    status: 'not_started',
    owner: 'Legal',
    policyRef: 'Privacy Policy §3 — Data Sharing',
    regulatoryBasis: ['HIPAA', 'MHMDA'],
    sla: null,
    implementationPhase: 2,
    firestoreCollection: 'dataops_third_party_shares',
    cloudFunction: null,
    lastVerified: null,
    notes: 'Vendors: CC Medical (covered entity), pharmacy partner, payment processor, analytics.',
    sortOrder: 51,
    updatedAt: now,
  },

  // ── DATA INVENTORY (System 7) ──
  {
    id: 'comp-data-map',
    category: 'data_inventory',
    label: 'User Data Inventory Map',
    description: 'Real-time inventory of where every user\'s data lives across Firestore, Storage, Auth, and third parties.',
    status: 'in_progress',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §1 — Data Collection',
    regulatoryBasis: ['MHMDA', 'CCPA'],
    sla: null,
    implementationPhase: 1,
    firestoreCollection: 'dataops_user_inventory',
    cloudFunction: 'data-inventory-sync',
    lastVerified: null,
    notes: 'Types + service layer ready. Needs Cloud Function for weekly scan across Firestore/Storage/Auth.',
    sortOrder: 60,
    updatedAt: now,
  },
  {
    id: 'comp-flag-resolution',
    category: 'data_inventory',
    label: 'Data Flag Resolution',
    description: 'Admin workflow to resolve data flags: missing consent records, stale data, unconfirmed third-party deletions, unverified encryption.',
    status: 'not_started',
    owner: 'Engineering',
    policyRef: 'Privacy Policy §4 — Security',
    regulatoryBasis: ['MHMDA'],
    sla: null,
    implementationPhase: 3,
    firestoreCollection: null,
    cloudFunction: null,
    lastVerified: null,
    notes: '4 flag types: noConsentRecord, staleData, thirdPartyUnconfirmed, encryptionUnverified.',
    sortOrder: 61,
    updatedAt: now,
  },
];
